import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { Modal } from 'react-bootstrap';
import { setModalHandlers } from '../utils/modalController';
import styles from './ModalProvider.module.scss';
import Button from '../components/Button';

type ModalType = 'alert' | 'confirm';

interface ModalContextType {
    alertModal: (message: string, title?: string) => Promise<void>;
    confirmModal: (message: string, title?: string) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) throw new Error('useModal deve estar dentro de ModalProvider');
    return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [visible, setVisible] = useState(false);
    const [message, setMessage] = useState('');
    const [title, setTitle] = useState('Atenção');
    const [type, setType] = useState<ModalType>('alert');
    const [resolver, setResolver] = useState<((value: any) => void) | null>(null);

    const alertModal = (msg: string, modalTitle = 'Atenção'): Promise<void> => {
        setMessage(msg);
        setTitle(modalTitle);
        setType('alert');
        setVisible(true);
        return new Promise((resolve) => setResolver(() => resolve));
    };

    const confirmModal = (
        msg: string,
        modalTitle = 'Confirmação'
    ): Promise<boolean> => {
        setMessage(msg);
        setTitle(modalTitle);
        setType('confirm');
        setVisible(true);
        return new Promise((resolve) => setResolver(() => resolve));
    };

    const close = () => {
        setVisible(false);
        if (resolver) {
            resolver(undefined); // alert resolve
            setResolver(null);
        }
    };

    const confirm = () => {
        setVisible(false);
        if (resolver) {
            resolver(true);
            setResolver(null);
        }
    };

    const cancel = () => {
        setVisible(false);
        if (resolver) {
            resolver(false);
            setResolver(null);
        }
    };

    useEffect(() => {
        // Define funções globais
        setModalHandlers({ alertModal, confirmModal });
    }, []);

    return (
        <ModalContext.Provider value={{ alertModal, confirmModal }}>
            {children}

            <Modal show={visible} onHide={close} backdrop="static" centered>
                <div className={styles['modal-backdrop']}>
                <Modal.Header closeButton className={styles['modal-header']}>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>

                <Modal.Body className={styles['modal-body']}>
                    {message.split('\n').map((line, idx) => (
                        <React.Fragment key={idx}>
                            {line}
                            <br />
                        </React.Fragment>
                    ))}
                </Modal.Body>

                <Modal.Footer className={styles['modal-footer']}>
                    {type === 'alert' && (
                        <Button
                            className={styles['modal-button']}
                            variant="primary"
                            onClick={close}>
                            OK
                        </Button>
                    )}

                    {type === 'confirm' && (
                        <>
                            <Button
                                className={styles['modal-button']}
                                variant="secondary"
                                onClick={cancel}>
                                Cancelar
                            </Button>
                            <Button
                                className={styles['modal-button']}
                                variant="primary"
                                onClick={confirm}>
                                Confirmar
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </div>
        </Modal>
        </ModalContext.Provider >
    );
};
